import React, { useState, useMemo } from 'react'
import { Switch } from '../ui/switch'
import { Position, Spinner, NumericInput } from '@blueprintjs/core'
import { Menu } from '../ui/menu'
import { HTMLSelect } from '../ui/html-select'
import { Toasts } from '../toaster/Toast'
import { Popover } from '../ui/popover'
import { Button } from '../ui/button'

import { State } from '../../constants'

const SetBudgetInput = ({ budget, setBudget, loading }) => {
    return (
        <NewNumericInput
            label="Budget"
            onValueChange={(e) => setBudget(e)}
            leftIcon="dollar"
            value={budget}
            disabled={loading}
        />
    )
}

const SetBidInput = ({ bid, setBid, loading }) => {
    return (
        <NewNumericInput
            label="Bid"
            onValueChange={(e) => setBid(e)}
            leftIcon="dollar"
            value={bid}
            disabled={loading}
        />
    )
}

const SetTacosInput = ({ tacos, setTacos, loading }) => {
    return (
        <NewNumericInput
            label="TACOS"
            onValueChange={(e) => setTacos(e)}
            leftIcon="percentage"
            value={tacos}
            disabled={loading}
        />
    )
}

// Currently not used
const AddToPortfolio = ({ portfolios }) => {
    const options = useMemo(() => {
        const output = []
        output.push(
            <option value={0} key={0}>
                None
            </option>,
        )
        portfolios.forEach((portfolio) => {
            output.push(
                <option value={portfolio.id} key={portfolio.id}>
                    {portfolio.name}
                </option>,
            )
        })
        return output
    }, [portfolios])

    return (
        <div className="grid grid-cols-3 gap-4">
            <div>
                <p className="pt-1">Add to portfolio</p>
            </div>
            <div className="col-span-2">
                <HTMLSelect>{options}</HTMLSelect>
            </div>
        </div>
    )
}

export const Actions = ({
    showBudget = false,
    showSmartBid = false,
    showTacos = false,
    showBid = true,
    showState = true,
    data,
    update,
    reloadParent,
    name,
}) => {
    const [showPopover, setShowPopover] = useState(false)
    const [loading, setLoading] = useState(false)
    const [dirtyActions, setDirtyActions] = useState({})
    const updateDirtyActions = (object) => {
        setDirtyActions({ ...dirtyActions, ...object })
    }

    const smart_bid = useMemo(() => {
        if (dirtyActions.hasOwnProperty('smart_bid')) {
            return dirtyActions.smart_bid == State.Enabled
        }

        return data?.smart_bid == State.Enabled
    }, [data?.smart_bid, dirtyActions?.smart_bid])

    const daily_budget = useMemo(() => {
        if (dirtyActions.hasOwnProperty('daily_budget')) {
            return dirtyActions.daily_budget
        }

        return data?.daily_budget / 100
    }, [data?.daily_budget, dirtyActions?.daily_budget])

    const target_acos = useMemo(() => {
        if (dirtyActions.hasOwnProperty('target_acos')) {
            return dirtyActions.target_acos
        }

        return data?.target_acos / 100
    }, [data?.target_acos, dirtyActions?.target_acos])

    const default_bid = useMemo(() => {
        if (dirtyActions.hasOwnProperty('default_bid')) {
            return dirtyActions.default_bid
        }

        if (dirtyActions.hasOwnProperty('bid')) {
            return dirtyActions.bid
        }

        if (data?.default_bid) {
            return data?.default_bid / 100
        }

        if (data?.bid) {
            return data?.bid / 100
        }
    }, [data?.default_bid, data?.bid, dirtyActions?.default_bid, dirtyActions?.bid])

    // this field can be default_bid or bid
    const resolvedBidFieldName = useMemo(() => {
        if (data?.bid) {
            return 'bid'
        }

        return 'default_bid'
    }, [data])

    const state = useMemo(() => {
        if (dirtyActions.hasOwnProperty('state')) {
            return dirtyActions.state == State.Enabled
        }

        return data?.state == State.Enabled
    }, [data?.state, dirtyActions?.state])

    const updateWrapper = () => {
        setLoading(true)
        const payload = formatDirtyActions()
        update(payload)
            .then((res) => {
                setLoading(false)
                if (name) {
                    Toasts.success(`${name} updated`)
                } else {
                    Toasts.success(`Update successful`)
                }
                setDirtyActions({})
                reloadParent()
                setShowPopover(false)
            })
            .catch((err) => {
                if (err.response.status === 409) {
                    if (name) {
                        Toasts.warning(
                            `This ${name} was out of sync. Please review the changes and retry.`,
                        )
                    } else {
                        Toasts.success(`Out of sync error. Please review the changes and retry.`)
                    }
                    if (reloadParent) {
                        reloadParent()
                    }
                    setLoading(false)
                    setDirtyActions({})
                }
            })
    }

    const formatDirtyActions = () => {
        const payload = { ...dirtyActions }
        if (payload.hasOwnProperty(resolvedBidFieldName)) {
            payload[resolvedBidFieldName] = payload[resolvedBidFieldName] * 100
        }
        if (payload.hasOwnProperty('daily_budget')) {
            payload.daily_budget = payload.daily_budget * 100
        }
        if (payload.hasOwnProperty('target_acos')) {
            payload.target_acos = payload.target_acos * 100
        }
        return payload
    }

    const menu = useMemo(() => {
        return (
            <div className="px-3 py-2" style={{ maxWidth: 270 }}>
                <Menu>
                    {showBudget && (
                        <div className="pb-1 px-1">
                            <SetBudgetInput
                                setBudget={(v) => updateDirtyActions({ daily_budget: v })}
                                budget={daily_budget}
                                loading={loading}
                            />
                        </div>
                    )}
                    {showBid && (
                        <div className="pb-1 px-1">
                            <SetBidInput
                                setBid={(v) => updateDirtyActions({ [resolvedBidFieldName]: v })}
                                bid={default_bid}
                                loading={loading}
                            />
                        </div>
                    )}
                    {/* {showTacos && <div className="pb-1 px-1">
                    <SetTacosInput
                        setTacos={v => updateDirtyActions({ target_acos: v })}
                        tacos={target_acos}
                    />
                </div>} */}
                    {showState && (
                        <div className="pb-1 px-1">
                            <Switch
                                checked={state}
                                onChange={(e) =>
                                    updateDirtyActions({
                                        state: e.target.checked ? State.Enabled : State.Paused,
                                    })
                                }
                                alignIndicator="right"
                                disabled={loading}
                                label="State"
                            />
                        </div>
                    )}
                    {showSmartBid && (
                        <div className="pb-1 px-1">
                            <Switch
                                checked={smart_bid}
                                onChange={(e) =>
                                    updateDirtyActions({ smart_bid: e.target.checked })
                                }
                                alignIndicator="right"
                                disabled={loading}
                                label="Smart Bid"
                            />
                        </div>
                    )}
                    <Button
                        disabled={Object.keys(dirtyActions).length === 0 || loading}
                        intent="primary"
                        text={loading ? <Spinner size={20} /> : 'Save'}
                        onClick={updateWrapper}
                    />
                </Menu>
            </div>
        )
    }, [daily_budget, dirtyActions, default_bid, state, target_acos, smart_bid])

    return (
        <Popover
            onClose={() => setShowPopover(false)}
            isOpen={showPopover}
            content={menu}
            position={Position.RIGHT_TOP}
        >
            <Button
                className="my-1"
                intent="primary"
                text="Actions"
                onClick={() => setShowPopover(true)}
            />
        </Popover>
    )
}

export const NewNumericInput = ({
    label,
    value = 0,
    leftIcon,
    onValueChange,
    stepSize = 0.1,
    disabled,
}) => {
    return (
        <div className="grid grid-cols-3 gap-4">
            <div>
                <p className="pt-1">{label}</p>
            </div>
            <div className="col-span-2">
                <NumericInput
                    disabled={disabled}
                    stepSize={stepSize}
                    leftIcon={leftIcon}
                    value={value}
                    onValueChange={(_, v) => onValueChange(v)}
                    fill
                />
            </div>
        </div>
    )
}
